import React, { useState, useMemo, useEffect, startTransition } from "react";
import styled, { css } from "styled-components";
import cloneDeep from "lodash/cloneDeep";
import useCurrentUser from "hooks/useCurrentUser";
import { Input, ControlLabel, Label, Button } from "components/bootstrap";
import { Select } from "components/common";
import { useUpdateInvestigations, useGetPriorities, useGetStatuses } from "security-app/hooks/useInvestigationsAPI";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { isPermitted } from "util/PermissionsMixin";
import UpdateEventStatusModal from "security-app/components/Investigations/plugin/shared/UpdateEventStatusModal";
import { useUsersAndTeamsOptions } from "../hooks/useUsersAndTeamsOptions";
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
const Row = styled.div(
  ({ $gap, $fullWidth }) => css`
    display: flex;
    flex-direction: row;
    gap: ${$gap || "1rem"};
    align-items: flex-end;

    & > * {
      width: ${$fullWidth ? "100%" : "auto"};
    }
  `
);
const FormGroup = styled.div`
  margin-bottom: 0 !important;

  & .form-group {
    margin-bottom: 0 !important;
    margin: 0;
  }
`;
const DefaultLabel = styled(Label)`
  display: inline-flex;
  margin-left: 5px;
  vertical-align: inherit;
`;
const INIT_INVESTIGATION = {
  name: "",
  assigned_to: "",
  priority: 0,
  status: ""
};
const REQURED_FIELDS = ["name"];
const initForm = (investigation = INIT_INVESTIGATION, priorityOptions = [], statusOptions = []) => Object.entries(INIT_INVESTIGATION).reduce((acc, [field, defaultValue]) => {
  if (field === "priority" && priorityOptions.length > 0) {
    const defaultPriority = priorityOptions.find((priority) => priority.default === true);
    const foundPriority = investigation[field] ? priorityOptions.find((priority) => priority.value === investigation[field].toString()) : void 0;
    let priorityValue;
    if (!foundPriority) {
      priorityValue = !investigation.name && defaultPriority ? defaultPriority.value : defaultValue;
    } else {
      priorityValue = foundPriority.value;
    }
    acc[field] = {
      name: field,
      value: priorityValue,
      valid: true,
      touched: false,
      required: false
    };
  } else if (field === "status" && statusOptions.length > 0) {
    const defaultStatus = statusOptions.find((status) => status.default === true);
    const foundStatus = investigation[field] ? statusOptions.find((status) => status.value === investigation[field]) : void 0;
    let statusValue;
    if (!foundStatus) {
      statusValue = !investigation.name && defaultStatus ? defaultStatus.value : defaultValue;
    } else {
      statusValue = foundStatus.value;
    }
    acc[field] = {
      name: field,
      value: statusValue,
      valid: true,
      touched: false,
      required: false
    };
  } else {
    acc[field] = {
      name: field,
      value: investigation[field] || defaultValue,
      valid: true,
      touched: false,
      required: false
    };
  }
  if (REQURED_FIELDS.includes(field)) {
    acc[field].valid = !!investigation[field];
    acc[field].required = true;
  }
  return acc;
}, {});
function Details({ investigation }) {
  const { permissions } = useCurrentUser();
  const [form, setForm] = useState(initForm(investigation));
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const sendTelemetry = useSendTelemetry();
  const canManageEvidence = useMemo(
    () => !investigation.archived && isPermitted(permissions, "investigations:edit"),
    [investigation.archived, permissions]
  );
  const { updateInvestigations, updatingInvestigations } = useUpdateInvestigations();
  const { priorities, loadingPriorities } = useGetPriorities();
  const priorityOptions = useMemo(
    () => priorities.map((priority) => ({
      value: priority.priority.toString(),
      label: priority.text,
      default: priority.default
    })).sort((a, b) => +b.value - +a.value),
    [priorities]
  );
  const { statuses, loadingStatuses } = useGetStatuses();
  const statusOptions = useMemo(
    () => statuses.map((status) => ({ value: status.status, label: status.status, default: status.default })).sort((a, b) => {
      if (a.value > b.value) return 1;
      if (a.value < b.value) return -1;
      return 0;
    }),
    [statuses]
  );
  useEffect(() => {
    if (!loadingPriorities && !loadingStatuses) {
      startTransition(() => {
        setForm(initForm(investigation, priorityOptions, statusOptions));
      });
    }
  }, [investigation, priorityOptions, loadingPriorities, statusOptions, loadingStatuses]);
  const assignToOptions = useUsersAndTeamsOptions();
  const isFormValid = useMemo(
    () => Object.values(form).every((field) => field.valid) && Object.values(form).some((field) => field.touched) && Object.values(form).some((field) => field.value),
    [form]
  );
  const handleSubmit = async (e) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_DETAILS_UPDATED, {
      app_pathname: "security",
      app_section: "investigation"
    });
    e.preventDefault();
    if (!isFormValid) return;
    const didStatusChange = form.status.value !== investigation.status && form.status.touched;
    const updatedInvestigation = Object.values(form).reduce(
      (acc, field) => {
        if (field.value !== null && field.value !== void 0) {
          acc[field.name] = field.value;
        }
        return acc;
      },
      { ...investigation }
    );
    await updateInvestigations({ investigations: [updatedInvestigation] });
    if (didStatusChange) {
      setShowUpdateStatusModal(true);
    }
  };
  const validateField = (value) => !!value && value.replace(/\s/g, "").length > 0;
  const updateForm = (e) => {
    if (e.preventDefault) e.preventDefault();
    const auxForm = cloneDeep(form);
    const { name: field, value } = e.target;
    auxForm[field].touched = true;
    auxForm[field].value = value;
    if (auxForm[field].required) auxForm[field].valid = validateField(value);
    setForm(auxForm);
  };
  const onBlur = (e) => {
    const auxForm = cloneDeep(form);
    const { name: field } = e.target;
    auxForm[field].touched = true;
    if (auxForm[field].required) auxForm[field].valid = validateField(auxForm[field].value);
    setForm(auxForm);
  };
  const renderOption = (option) => /* @__PURE__ */ React.createElement("span", { key: option.value, title: option.value }, option.label, option.default && /* @__PURE__ */ React.createElement(DefaultLabel, { bsStyle: "primary", bsSize: "xsmall" }, "Default"));
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Form, { onSubmit: handleSubmit }, /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: form.name.name,
      name: form.name.name,
      type: "text",
      label: "Name",
      "data-testid": form.name.name,
      required: form.name.required,
      value: form.name.value,
      error: !form.name.valid && form.name.touched ? "Required" : "",
      bsStyle: !form.name.valid && form.name.touched ? "error" : null,
      onChange: updateForm,
      onBlur,
      readOnly: !canManageEvidence
    }
  )), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(ControlLabel, { htmlFor: form.assigned_to.name }, "Assign To"), /* @__PURE__ */ React.createElement(
    Select,
    {
      inputId: form.assigned_to.name,
      inputProps: { name: form.assigned_to.name, "data-testid": form.assigned_to.name },
      placeholder: "Select user or team...",
      onChange: (value) => updateForm({ target: { name: form.assigned_to.name, value } }),
      value: form.assigned_to.value,
      options: assignToOptions,
      onBlur,
      disabled: !canManageEvidence
    }
  )), /* @__PURE__ */ React.createElement(Row, { $fullWidth: true }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(ControlLabel, { htmlFor: form.priority.name }, "Priority"), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: form.priority.name,
      inputProps: { name: form.priority.name, "data-testid": form.priority.name },
      placeholder: "Select priority ...",
      onChange: (value) => updateForm({ target: { name: form.priority.name, value: +value } }),
      value: form.priority.value ? form.priority.value.toString() : null,
      options: priorityOptions,
      onBlur,
      disabled: !canManageEvidence,
      optionRenderer: (option) => renderOption(option)
    }
  )), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(ControlLabel, { htmlFor: form.status.name }, "Status"), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: form.status.name,
      inputProps: { name: form.status.name, "data-testid": form.status.name },
      placeholder: "Select status ...",
      onChange: (value) => updateForm({ target: { name: form.status.name, value } }),
      value: form.status.value,
      options: statusOptions,
      onBlur,
      disabled: !canManageEvidence,
      optionRenderer: (option) => renderOption(option)
    }
  )), canManageEvidence && /* @__PURE__ */ React.createElement("div", { style: { width: "auto" } }, /* @__PURE__ */ React.createElement(
    Button,
    {
      type: "submit",
      bsStyle: "primary",
      onClick: handleSubmit,
      disabled: !isFormValid || updatingInvestigations
    },
    "Save"
  )))), showUpdateStatusModal && /* @__PURE__ */ React.createElement(
    UpdateEventStatusModal,
    {
      investigationIds: [investigation.id],
      show: showUpdateStatusModal,
      onClose: () => {
        setShowUpdateStatusModal(false);
      }
    }
  ));
}
export default Details;
